/**
 * Compras y Boletos del socio · sincronización con backend real.
 *
 * Fuente de verdad: FastAPI (`/api/compras`, `/api/boletos`, `/api/socios/me/*`).
 * Cache local en `localStorage` bajo `ct_compras_v1` / `ct_boletos_v1` para
 * lecturas síncronas de la UI (compatibilidad con componentes existentes).
 *
 * Todas las escrituras son async. `hidratarComprasBoletos()` refresca el
 * cache al montar las pantallas relevantes.
 */

import { api } from "@/services/api";

const KEY_COMPRAS = "ct_compras_v1";
const KEY_BOLETOS = "ct_boletos_v1";
const KEY_CURRENT_SOCIO = "ct_current_socio_v1";

export const ESTADO_BOLETO_INICIAL = "PENDIENTE_DE_ASIGNAR";

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

// Guarda liviana: si hay snapshot del socio, la cookie HttpOnly también
// debería existir. El backend rechaza con 401 si no.
const haySesionSocio = () => {
  if (!isBrowser()) return false;
  try {
    const raw = window.localStorage.getItem(KEY_CURRENT_SOCIO);
    if (!raw) return false;
    const s = JSON.parse(raw);
    return !!(s && s.id);
  } catch {
    return false;
  }
};

const readJson = (key, fb) => {
  if (!isBrowser()) return fb;
  try {
    const r = window.localStorage.getItem(key);
    return r ? JSON.parse(r) : fb;
  } catch {
    return fb;
  }
};

const writeJson = (key, value) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

/* -------------------- Compat: socio activo -------------------- */

/** @deprecated Usa `socioAuthStore.getCurrentSocio()`. */
export const getCurrentSocio = () => {
  const s = readJson(KEY_CURRENT_SOCIO, null);
  if (s && s.id && haySesionSocio()) return s;
  return { id: "invitado", nombre: "Invitado" };
};

/** @deprecated Login se hace en `socioAuthStore.loginSocio()`. */
export const setCurrentSocio = () => {
  /* no-op: la sesión real vive en `socioAuthStore`. */
  return getCurrentSocio();
};

/* -------------------- Hidratación -------------------- */

/** true sólo si el servidor confirmó compras y boletos; false conserva el cache. */
export const hidratarComprasBoletos = async () => {
  if (!haySesionSocio()) return false;
  try {
    const [{ data: compras }, { data: boletos }] = await Promise.all([
      api.get("/socios/me/compras"),
      api.get("/socios/me/boletos"),
    ]);
    if (!Array.isArray(compras) || !Array.isArray(boletos)) return false;
    writeJson(KEY_COMPRAS, compras);
    writeJson(KEY_BOLETOS, boletos);
    return true;
  } catch {
    return false;
  }
};

/* -------------------- Reads (sync desde cache) -------------------- */

const readCompras = () => readJson(KEY_COMPRAS, []) || [];
const readBoletos = () => readJson(KEY_BOLETOS, []) || [];

export const getComprasBySocio = (socioId) => {
  if (!socioId) return [];
  return readCompras()
    .filter((c) => c.socioId === socioId)
    .sort(
      (a, b) =>
        new Date(b.fechaCompra || 0).getTime() -
        new Date(a.fechaCompra || 0).getTime(),
    );
};

// Sprint 54 · Fase A: recupera una compra desde el BACKEND (fuente de
// verdad) por id estable. Úsalo cuando el cache local aún no tenga la
// compra (recarga F5, pestaña nueva, respuesta asíncrona lenta).
export const obtenerCompraBackend = async (compraId) => {
  if (!compraId) return null;
  try {
    const { data } = await api.get(`/compras/${encodeURIComponent(compraId)}`);
    return data;
  } catch {
    return null;
  }
};

export const getBoletosByCompra = (compraId) => {
  if (!compraId) return [];
  return readBoletos()
    .filter((b) => b.compraId === compraId)
    .sort((a, b) => a.consecutivo - b.consecutivo);
};

export const getBoletosBySocio = (socioId) => {
  if (!socioId) return [];
  return readBoletos().filter((b) => b.socioId === socioId);
};

export const getBoletoById = (boletoId, socioId) => {
  if (!boletoId || !socioId) return null;
  const b = readBoletos().find((x) => x.id === boletoId);
  if (!b) return null;
  if (b.socioId !== socioId) return null;
  return b;
};

export const contarPendientes = (compraId) =>
  getBoletosByCompra(compraId).filter((b) => b.estado !== "LISTO" && b.estado !== "VALIDADO").length;

export const contarSinNombre = (compraId) =>
  getBoletosByCompra(compraId).filter(
    (b) => !b.asistenteNombre || String(b.asistenteNombre).trim().length === 0,
  ).length;

/**
 * Sprint 54 · Fase A · Agrupación de "Mis Boletos" por evento.
 *
 * Sólo presentación/consulta: las compras se mantienen separadas en el
 * backend (auditoría, pagos, idempotencia). Aquí se consolidan las
 * compras CONFIRMADAS del mismo `eventoId` en una sola tarjeta con:
 * total de boletos, total pagado acumulado, salones/mesas involucrados,
 * y contadores de asignados/pendientes. Conserva el desglose por compra.
 */
export const agruparComprasPorEvento = (socioId) => {
  if (!socioId) return [];
  const compras = getComprasBySocio(socioId).filter(
    (c) => c.estado === "CONFIRMADA",
  );
  const boletos = getBoletosBySocio(socioId);
  const mapa = new Map();
  compras.forEach((c) => {
    const g = mapa.get(c.eventoId) || {
      eventoId: c.eventoId,
      nombreEvento: c.nombreEvento,
      fechaEvento: c.fechaEvento,
      horaEvento: c.horaEvento,
      imagen: c.imagen,
      salones: [],
      mesas: [],
      totalBoletos: 0,
      totalPagado: 0,
      simulado: false,
      compras: [],
      _fechaMax: 0,
    };
    if (c.salon && !g.salones.includes(c.salon)) g.salones.push(c.salon);
    if (c.mesa !== null && c.mesa !== undefined) {
      const mesaNum = Number(c.mesa);
      if (Number.isFinite(mesaNum) && !g.mesas.includes(mesaNum))
        g.mesas.push(mesaNum);
    }
    g.totalBoletos += Number(c.cantidad) || 0;
    g.totalPagado += Number(c.total) || 0;
    if (c.simulado) g.simulado = true;
    g.compras.push(c);
    g._fechaMax = Math.max(
      g._fechaMax,
      new Date(c.fechaCompra || 0).getTime() || 0,
    );
    mapa.set(c.eventoId, g);
  });
  return Array.from(mapa.values())
    .map((g) => {
      const bs = boletos.filter((b) => b.eventoId === g.eventoId);
      const asignados = bs.filter(
        (b) => (b.asistenteNombre || "").trim().length > 0,
      ).length;
      return {
        ...g,
        mesas: g.mesas.slice().sort((a, b) => a - b),
        totalBoletosReales: bs.length,
        asignados,
        pendientes: bs.length - asignados,
      };
    })
    .sort((a, b) => b._fechaMax - a._fechaMax);
};

/* -------------------- Writes (async · backend) -------------------- */

const _upsertCompra = (compra) => {
  if (!compra || !compra.id) return;
  const arr = readCompras();
  const idx = arr.findIndex((c) => c.id === compra.id);
  if (idx >= 0) arr[idx] = compra;
  else arr.unshift(compra);
  writeJson(KEY_COMPRAS, arr);
};

const _upsertBoleto = (boleto) => {
  if (!boleto || !boleto.id) return;
  const arr = readBoletos();
  const idx = arr.findIndex((b) => b.id === boleto.id);
  if (idx >= 0) arr[idx] = boleto;
  else arr.push(boleto);
  writeJson(KEY_BOLETOS, arr);
};

/**
 * Registra una compra confirmada en el backend con `idempotencyKey`.
 * Devuelve el objeto persistido o `{ error }` si falla.
 * Después de una compra exitosa, refresca compras y boletos en cache.
 */
export const registrarCompra = async (datos) => {
  if (!datos) return null;
  const idempotencyKey =
    datos.idempotencyKey ||
    datos.pagoId ||
    (datos.pago && datos.pago.id) ||
    `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const payload = {
    idempotencyKey,
    eventoId: datos.eventoId,
    mesa: Number(datos.mesa),
    cantidad: Number(datos.cantidad),
    precioPorBoleto: Number(datos.precioPorBoleto || 0),
    subtotal: Number(datos.subtotal || datos.total || 0),
    cargoServicio: Number(datos.cargoServicio || 0),
    total: Number(datos.total || 0),
    moneda: datos.moneda || "MXN",
    pago: {
      id: datos.pagoId || (datos.pago && datos.pago.id) || "",
      referencia: datos.referencia || (datos.pago && datos.pago.referencia) || "",
      simulado: datos.simulado !== undefined ? !!datos.simulado : !!(datos.pago && datos.pago.simulado),
    },
  };

  try {
    const { data } = await api.post("/compras", payload);
    _upsertCompra(data);
    // Refresh boletos silencioso.
    await hidratarComprasBoletos();
    return data;
  } catch (err) {
    // El backend devuelve `detail` como objeto {reason, detail,...} para
    // errores de negocio (límite, disponibilidad, venta pausada) y como
    // string para errores genéricos. Normalizamos a una forma estable.
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object") {
      return {
        error: detail.reason || "negocio",
        mensaje: detail.detail || "No pudimos completar la compra.",
        status,
        meta: detail,
      };
    }
    return {
      error: typeof detail === "string" ? "negocio" : "network",
      mensaje: typeof detail === "string" ? detail : "",
      status,
    };
  }
};

/**
 * Sprint 57 · F4B · Crea una compra ACCESO_GENERAL multisalón.
 *
 * Envía EXACTAMENTE `{ eventoId, items, idempotencyKey }`. NO envía mesa,
 * modalidad, precio, subtotal ni total: el backend es la única autoridad
 * monetaria y de disponibilidad. Devuelve la Compra persistida (con `items`)
 * o `{ error, mensaje, status, meta }` normalizado.
 */
export const crearCompraAG = async ({ eventoId, items, idempotencyKey }) => {
  if (!eventoId || !Array.isArray(items)) return { error: "sin-datos" };
  // Filtrar cantidad 0 y mantener orden determinista por salonAccesoId.
  const itemsLimpios = items
    .filter((it) => it && it.salonAccesoId && Number(it.cantidad) > 0)
    .map((it) => ({ salonAccesoId: it.salonAccesoId, cantidad: Number(it.cantidad) }))
    .sort((a, b) => String(a.salonAccesoId).localeCompare(String(b.salonAccesoId)));
  if (itemsLimpios.length === 0) return { error: "sin-items" };
  try {
    const { data } = await api.post("/compras", {
      eventoId,
      items: itemsLimpios,
      idempotencyKey,
    });
    _upsertCompra(data);
    return data;
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object") {
      return {
        error: detail.reason || "negocio",
        mensaje: detail.detail || "No pudimos completar la compra.",
        status,
        meta: detail,
      };
    }
    return {
      error: typeof detail === "string" ? "negocio" : "network",
      mensaje: typeof detail === "string" ? detail : "",
      status,
    };
  }
};

/**
 * Sprint 55 · Contexto de compra autoritativo desde el backend:
 * disponibilidad real por mesa + cupo personal restante del socio.
 * Devuelve el objeto del backend o `{ error }` si falla.
 */
export const obtenerCompraContexto = async (eventoId) => {
  if (!eventoId) return { error: "sin-evento" };
  try {
    const { data } = await api.get(
      `/eventos/${encodeURIComponent(eventoId)}/compra-contexto`,
    );
    return data;
  } catch (err) {
    return { error: err?.response?.data?.detail || "network" };
  }
};

/**
 * Actualiza el nombre del asistente y trigger de rotación de qrToken.
 * El backend aplica todas las reglas de QR (histórico, REEMPLAZADO,
 * bloqueo si VALIDADO).
 */
export const actualizarAsistente = async (boletoId, socioId, { nombre }) => {
  if (!boletoId) return null;
  try {
    const { data } = await api.patch(`/boletos/${boletoId}/asistente`, {
      nombre: nombre || "",
    });
    _upsertBoleto(data);
    return data;
  } catch (err) {
    if (err?.response?.status === 409) {
      return { __bloqueado: true, boleto: await recargarBoleto(boletoId) };
    }
    return null;
  }
};

/** Relee un boleto del servidor y actualiza el cache; null si no se pudo. */
export const recargarBoleto = async (boletoId) => {
  try {
    const { data } = await api.get(`/boletos/${boletoId}`);
    _upsertBoleto(data);
    return data;
  } catch {
    return null;
  }
};

/* -------------------- Recepción (Ola 4 · sigue local) -------------------- */

const KEY_BOLETOS_LOCAL_LEGACY = "ct_boletos_v1";

/**
 * Estos helpers los sigue usando el portal de Recepción (Ola 4).
 * Operan sobre el cache local (que ahora se hidrata desde backend).
 * Cuando Ola 4 migre, se moverán al backend.
 */
export const getEventosConBoletosEmitidos = () => {
  const boletos = readJson(KEY_BOLETOS_LOCAL_LEGACY, []) || [];
  const mapa = new Map();
  boletos.forEach((b) => {
    if (!b.eventoId) return;
    const bucket = mapa.get(b.eventoId) || {
      eventoId: b.eventoId,
      nombreEvento: b.nombreEvento,
      fechaEvento: b.fechaEvento,
      horaEvento: b.horaEvento,
      salon: b.salon,
      total: 0,
      validados: 0,
      listos: 0,
      sinAsignar: 0,
    };
    bucket.total += 1;
    if (b.estado === "VALIDADO") bucket.validados += 1;
    else if (b.estado === "LISTO") bucket.listos += 1;
    else bucket.sinAsignar += 1;
    mapa.set(b.eventoId, bucket);
  });
  return Array.from(mapa.values()).sort((a, b) =>
    (a.nombreEvento || "").localeCompare(b.nombreEvento || ""),
  );
};

export const buscarBoletoPorQrToken = (qrToken) => {
  if (!qrToken || typeof qrToken !== "string") return null;
  const token = qrToken.trim();
  if (!token) return null;
  const boletos = readJson(KEY_BOLETOS_LOCAL_LEGACY, []) || [];
  const activo = boletos.find((b) => b.qrToken === token);
  if (activo) return { boleto: activo, tokenActivo: true };
  for (const b of boletos) {
    if (Array.isArray(b.qrHistorial)) {
      const hit = b.qrHistorial.find((h) => h && h.token === token);
      if (hit) return { boleto: b, tokenActivo: false, entradaHistorial: hit };
    }
  }
  return null;
};

const normalizar = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export const buscarBoletosPorNombre = (eventoId, query) => {
  const q = normalizar(query);
  if (!eventoId || q.length < 2) return [];
  const boletos = readJson(KEY_BOLETOS_LOCAL_LEGACY, []) || [];
  return boletos
    .filter((b) => b.eventoId === eventoId)
    .filter((b) => normalizar(b.asistenteNombre).includes(q))
    .sort((a, b) => a.consecutivo - b.consecutivo);
};

const _marcarLocal = (boletoId, metodoValidacion) => {
  const boletos = readJson(KEY_BOLETOS_LOCAL_LEGACY, []) || [];
  const idx = boletos.findIndex((b) => b.id === boletoId);
  if (idx < 0) {
    return { resultado: "RECHAZADO", motivo: "Boleto no encontrado.", boleto: null };
  }
  const b = boletos[idx];
  if (b.estado === "VALIDADO") {
    return { resultado: "RECHAZADO", motivo: "Boleto ya utilizado.", boleto: b };
  }
  const now = new Date().toISOString();
  const actualizado = {
    ...b,
    estado: "VALIDADO",
    qrEstado: b.qrEstado === "VIGENTE" ? "CONSUMIDO" : b.qrEstado,
    fechaValidacion: now,
    metodoValidacion,
  };
  boletos[idx] = actualizado;
  writeJson(KEY_BOLETOS_LOCAL_LEGACY, boletos);
  return {
    resultado: "OK",
    motivo:
      metodoValidacion === "QR"
        ? "Acceso autorizado."
        : "Acceso autorizado (validación manual).",
    boleto: actualizado,
  };
};

export const validarBoletoPorQR = (qrToken, opciones = {}) => {
  const { eventoId = null, forzar = false } = opciones;
  const encontrado = buscarBoletoPorQrToken(qrToken);
  if (!encontrado) {
    return { resultado: "RECHAZADO", motivo: "Código QR no reconocido.", boleto: null };
  }
  const { boleto, tokenActivo, entradaHistorial } = encontrado;
  if (!tokenActivo) {
    return {
      resultado: "RECHAZADO",
      motivo: "Este código QR fue reemplazado por uno nuevo. Solicita al asistente el código vigente.",
      boleto,
      entradaHistorial,
    };
  }
  if (boleto.estado === "VALIDADO") {
    return {
      resultado: "RECHAZADO",
      motivo: `Boleto ya utilizado${
        boleto.fechaValidacion ? ` (${new Date(boleto.fechaValidacion).toLocaleString("es-MX")})` : ""
      }.`,
      boleto,
    };
  }
  if (eventoId && boleto.eventoId !== eventoId) {
    return { resultado: "ADVERTENCIA", motivo: "El boleto pertenece a un evento distinto al seleccionado.", boleto };
  }
  if (!boleto.asistenteNombre && !forzar) {
    return { resultado: "ADVERTENCIA", motivo: "Boleto sin nombre de asistente asignado.", boleto };
  }
  return _marcarLocal(boleto.id, "QR");
};

export const validarBoletoManual = (boletoId, opciones = {}) => {
  const { eventoId = null, forzar = false } = opciones;
  const boletos = readJson(KEY_BOLETOS_LOCAL_LEGACY, []) || [];
  const boleto = boletos.find((b) => b.id === boletoId);
  if (!boleto) {
    return { resultado: "RECHAZADO", motivo: "Boleto no encontrado.", boleto: null };
  }
  if (boleto.estado === "VALIDADO") {
    return {
      resultado: "RECHAZADO",
      motivo: `Boleto ya utilizado${
        boleto.fechaValidacion ? ` (${new Date(boleto.fechaValidacion).toLocaleString("es-MX")})` : ""
      }.`,
      boleto,
    };
  }
  if (eventoId && boleto.eventoId !== eventoId) {
    return { resultado: "ADVERTENCIA", motivo: "El boleto pertenece a un evento distinto al seleccionado.", boleto };
  }
  if (!boleto.asistenteNombre && !forzar) {
    return { resultado: "ADVERTENCIA", motivo: "Boleto sin nombre de asistente asignado.", boleto };
  }
  return _marcarLocal(boleto.id, "MANUAL");
};

export const isEmailValido = () => true; // Legacy stub
